import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { spacing } from "#app/theme/v2/spacing";
import { IconButton } from "#components/atoms/IconButton/IconButton";
import { Tooltip } from "#components/atoms/Tooltip/Tooltip";
import { Select } from "#components/molecules/Select/Select";
import { createTableColumnHelper } from "#components/organisms/Table/tableHook";
import { OnboardingProcessStep } from "#modules/onboardingV2/infra/fetchOnboardingProcessSteps.api";
import { UpdateOnboardingProcessStepDto } from "#modules/onboardingV2/infra/updateOnboardingProcessStep.api";
import { DurationCell } from "#modules/onboardingV2/view/processStepConfiguration/DurationCell";
import { MachineNameCell } from "#modules/onboardingV2/view/processStepConfiguration/MachineNameCell";
import {
  processStepIndustrialProcessOptions,
  processStepMethodOptions,
  processStepTypeOptions,
} from "#modules/onboardingV2/view/processStepConfiguration/processStepConfigurationLabels";
import { ProcessStepPriceCell } from "#modules/onboardingV2/view/processStepConfiguration/ProcessStepPriceCell";
import { getIndustrialProcessWording } from "#shared/helpers/getIndustrialProcessWording";
import { Icon } from "#shared/icons/Icon";
import {
  IndustrialProcessType,
  resolveOnboardingProcessStepTarget,
  SurfaceTreatmentMethodSchema,
  SurfaceTreatmentMethodType,
  SurfaceTreatmentTypeType,
} from "shared";

const columnHelper = createTableColumnHelper<OnboardingProcessStep>();

type DurationField = "preparationTimeFormula" | "cycleTimeFormula";

export type DurationModalTarget = {
  id: string;
  field: DurationField;
  value: string | null;
  title: string;
};

export const useProcessStepConfigurationColumns = (
  onUpdateProcessStep: (id: string, payload: UpdateOnboardingProcessStepDto) => void,
  onDeleteProcessStep: (processStep: OnboardingProcessStep) => void,
  onOpenDurationModal: (target: DurationModalTarget) => void,
  showValidationErrors: boolean,
) => {
  const { t } = useTranslation();

  const industrialProcessOptions = useMemo(() => processStepIndustrialProcessOptions(), []);
  const typeOptions = useMemo(() => processStepTypeOptions(t), [t]);
  const methodOptions = useMemo(() => processStepMethodOptions(t), [t]);

  return useMemo(
    () => [
      columnHelper.accessor("industrialProcess", {
        id: "industrialProcess",
        header: () => t("onboardingV2.processStepConfiguration.columns.industrialProcess"),
        meta: {
          verticalPadding: spacing[0],
        },
        size: 196,
        sortFn: (rowA, rowB) =>
          getIndustrialProcessWording(rowA.original.industrialProcess).localeCompare(
            getIndustrialProcessWording(rowB.original.industrialProcess),
          ),
        cell: ({ row: { original } }) => (
          <Select
            value={original.industrialProcess}
            onChange={(value) =>
              value &&
              onUpdateProcessStep(original.id, {
                industrialProcess: value as IndustrialProcessType,
              })
            }
            data={industrialProcessOptions}
          />
        ),
      }),
      columnHelper.display({
        id: "type",
        header: () => t("onboardingV2.processStepConfiguration.columns.type"),
        size: 108,
        meta: {
          verticalPadding: spacing[0],
        },
        cell: ({ row: { original } }) => (
          <Select
            value={original.type}
            onChange={(value) => {
              if (!value) {
                return;
              }

              const nextType = value as SurfaceTreatmentTypeType;
              const willLockToTime =
                resolveOnboardingProcessStepTarget(original.industrialProcess, nextType) ===
                "MACHINE";

              onUpdateProcessStep(original.id, {
                type: nextType,
                ...(willLockToTime && { preparationTimeFormula: null, cycleTimeFormula: null }),
              });
            }}
            data={typeOptions}
            clearable={false}
          />
        ),
      }),
      columnHelper.display({
        id: "machineName",
        header: () => t("onboardingV2.processStepConfiguration.columns.machine"),
        size: 210,
        meta: {
          verticalPadding: spacing[0],
        },
        cell: ({ row: { original } }) => (
          <MachineNameCell
            value={original.machineName}
            onUpdate={(machineName) => onUpdateProcessStep(original.id, { machineName })}
          />
        ),
      }),
      columnHelper.display({
        id: "method",
        header: () => t("onboardingV2.processStepConfiguration.columns.method"),
        size: 142,
        meta: {
          verticalPadding: spacing[0],
        },
        cell: ({ row: { original } }) => {
          const isLockedToTime =
            resolveOnboardingProcessStepTarget(original.industrialProcess, original.type) ===
            "MACHINE";

          return (
            <Select
              value={original.method}
              disabled={isLockedToTime}
              onChange={(value) =>
                value &&
                onUpdateProcessStep(original.id, { method: value as SurfaceTreatmentMethodType })
              }
              data={methodOptions}
            />
          );
        },
      }),
      columnHelper.display({
        id: "price",
        header: () => t("onboardingV2.processStepConfiguration.columns.price"),
        size: 176,
        meta: {
          verticalPadding: spacing[0],
        },
        cell: ({ row: { original } }) => (
          <ProcessStepPriceCell
            value={original.price}
            method={original.method}
            showValidationErrors={showValidationErrors}
            onUpdate={(price) => onUpdateProcessStep(original.id, { price })}
          />
        ),
      }),
      columnHelper.display({
        id: "preparationTimeFormula",
        header: () => t("onboardingV2.processStepConfiguration.columns.preparationTimeFormula"),
        size: 176,
        meta: {
          verticalPadding: spacing[0],
        },
        cell: ({ row: { original } }) => {
          const title = t("onboardingV2.processStepConfiguration.columns.preparationTimeFormula");
          return (
            <DurationCell
              value={original.preparationTimeFormula}
              editable={original.method === SurfaceTreatmentMethodSchema.enum.time}
              title={title}
              onOpen={() =>
                onOpenDurationModal({
                  id: original.id,
                  field: "preparationTimeFormula",
                  value: original.preparationTimeFormula,
                  title,
                })
              }
            />
          );
        },
      }),
      columnHelper.display({
        id: "cycleTimeFormula",
        header: () => t("onboardingV2.processStepConfiguration.columns.cycleTimeFormula"),
        size: 176,
        meta: {
          verticalPadding: spacing[0],
        },
        cell: ({ row: { original } }) => {
          const title = t("onboardingV2.processStepConfiguration.columns.cycleTimeFormula");
          return (
            <DurationCell
              value={original.cycleTimeFormula}
              editable={original.method === SurfaceTreatmentMethodSchema.enum.time}
              title={title}
              onOpen={() =>
                onOpenDurationModal({
                  id: original.id,
                  field: "cycleTimeFormula",
                  value: original.cycleTimeFormula,
                  title,
                })
              }
            />
          );
        },
      }),
      columnHelper.display({
        id: "delete",
        header: () => null,
        meta: {
          verticalPadding: spacing[0],
          horizontalPadding: spacing[0],
        },
        size: 32,
        cell: ({ row: { original } }) => (
          <Tooltip label={t("common.delete")}>
            <IconButton
              variant="transparent"
              size="sm"
              aria-label={t("common.delete")}
              onClick={() => onDeleteProcessStep(original)}
            >
              <Icon name="trash" size={16} />
            </IconButton>
          </Tooltip>
        ),
      }),
    ],
    [
      t,
      industrialProcessOptions,
      typeOptions,
      methodOptions,
      showValidationErrors,
      onUpdateProcessStep,
      onDeleteProcessStep,
      onOpenDurationModal,
    ],
  );
};
